import { useAsync } from '@shared/async';
import { PaginationResult } from '@shared/pagination';
import * as DateValidators from '@shared/validators/date-time-validators';
import { useQuery } from '@tanstack/react-query';
import { Box, CheckBox, FormField, Text } from 'grommet';
import React, { ReactNode, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Chip } from '@/components/chip';
import { ClassificationFilter } from '@/components/classification-filter';
import { ClinicalsProductFilter } from '@/components/clinicals-product-filter';
import { RhfFilterForm } from '@/components/filters';
import { DateTextInput, InMemoryMultiSelectList, LazyLoadMultiSelectList } from '@/components/form-controls';
import { MultiSelectOption } from '@/components/form-controls/multi-select-list';
import { StateAndPoolFilter } from '@/components/state-and-pool-filter';
import { PdlStatus, pdlStatusOptions } from '@/features/coverage/types/pdl-status';
import { useDebounce } from '@/hooks/use-debounce';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { Lookup } from '@/types/lookup';
import { Listbox, ListboxOption } from '@/components-new/listbox';
import { ListboxWithPlaceHolder } from '@/components-new/listbox-with-placeholder';

const STEP_THERAPY_COUNT_OPTIONS: Lookup<string>[] = Array.from(
  { length: 11 },
  (_, i): Lookup<string>[] => [
    { id: String(i), label: String(i) },
    { id: `${i}+`, label: `${i}+` },
  ]
).flat();

export type FilterInputs = {
  clients?: MultiSelectOption[];
  states?: MultiSelectOption<string>[];
  products?: MultiSelectOption[];
  classifications?: MultiSelectOption[];
  paTypes?: MultiSelectOption[];
  pdlStatuses?: MultiSelectOption<PdlStatus>[];
  coverageTags?: MultiSelectOption[];
  stepTherapyCounts?: MultiSelectOption<string>[];
  pdlStatusBeforeDate?: string;
  excludeNonTrackedDrugs?: boolean;
  isStatePaCriteriaSameAsPdlLink?: string;
};

type CoverageFiltersProps = {
  disabled: boolean;
  defaultFilters: FilterInputs;
  onApplyFilters: (filters: FilterInputs) => void;
  children?: ReactNode;
};

export const CoverageFilters = (props: CoverageFiltersProps) => {
  const {
    defaultFilters,
    onApplyFilters,
    children
  } = props;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset
  } = useForm<FilterInputs>({
    mode: 'all',
    defaultValues: {
      clients: defaultFilters.clients ?? [],
      states: defaultFilters.states ?? [],
      products: defaultFilters.products ?? [],
      classifications: defaultFilters.classifications ?? [],
      paTypes: defaultFilters.paTypes ?? [],
      pdlStatuses: defaultFilters.pdlStatuses ?? [],
      coverageTags: defaultFilters.coverageTags ?? [],
      stepTherapyCounts: defaultFilters.stepTherapyCounts ?? [],
      pdlStatusBeforeDate: defaultFilters.pdlStatusBeforeDate ?? '',
      excludeNonTrackedDrugs: true,
      isStatePaCriteriaSameAsPdlLink: ''
    }
  });

  const handleFilterSubmit = async (filters: FilterInputs) => {
    onApplyFilters(filters);
  };

  const filters = watch();
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const anySelectedFilters = filters.clients.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.states.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.products.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.classifications.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.paTypes.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.pdlStatuses.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.coverageTags.length > 0
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    || filters.stepTherapyCounts.length > 0
    || filters.isStatePaCriteriaSameAsPdlLink;

  const { getStates, getPools, getClientLookups, getPaTypeLookups, getClassifications, getAllCoverageTags } = useLookupsService();

  const statesAsync = useAsync(getStates);
  const poolsAsync = useAsync(getPools);
  const paTypesAsync = useAsync(getPaTypeLookups);
  const coverageTagsAsync = useAsync(getAllCoverageTags)

  useEffect(() => {
    void statesAsync.execute();
    void poolsAsync.execute();
    void paTypesAsync.execute();
    void coverageTagsAsync.execute();
  }, []);

  const [classifications, clients] = watch(['classifications', 'clients']);
  const selectedClassifications = useDebounce(classifications);
  const selectedClients = useDebounce(clients);

  const clientIds = selectedClients?.map(client => client.id) ?? [];

  const { data: filteredClassifications } = useQuery({
    queryKey: ['classifications', clientIds],
    queryFn: async () => {
      const data = await getClassifications({
        trackedByClients: clientIds.length ? clientIds : undefined
      });
      if (!data || data.length === 0) {
        setValue('classifications', []);
      }
      return data;
    }
  });

  return (
    <RhfFilterForm
      variant="table"
      actions={children}
      watch={watch}
      onReset={() => reset({
        clients: [],
        states: [],
        products: [],
        classifications: [],
        paTypes: [],
        pdlStatuses: [],
        coverageTags: [],
        stepTherapyCounts: [],
        pdlStatusBeforeDate: '',
      })}
      onSubmit={handleSubmit(handleFilterSubmit)}
      chips={anySelectedFilters ? (
        <>
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.states.map(({ id, label }) => (
            <Chip
              key={`states-${id}`}
              chip={{
                label: (
                  <>
                    <strong>State/Pool:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('states', filters.states.filter(option => option.id !== id))}
            />
          ))}
          {/*@ts-expect-error TS(2532): Object is possibly 'undefined'.*/}
          {filters.clients.map(({ id, label }) => (
            <Chip
              key={`clients-${id}`}
              chip={{
                label: (
                  <>
                    <strong>Client:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('clients', filters.clients.filter(option => option.id !== id))}
            />
          ))}
          {/*@ts-expect-error TS(2532): Object is possibly 'undefined'.*/}
          {filters.products.map(({ id, label }) => (
            <Chip
              key={`products-${id}`}
              chip={{
                label: (
                  <>
                    <strong>Drug:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('products', filters.products.filter(option => option.id !== id))}
            />
          ))}
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.classifications.map(({ id, label }) => (
            <Chip
              key={`classifications-${id}`}
              chip={{
                label: (
                  <>
                    <strong>Class:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('classifications', filters.classifications.filter(option => option.id !== id))}
            />
          ))}
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.paTypes.map(({ id, label }) => (
            <Chip
              key={`paTypes-${id}`}
              chip={{
                label: (
                  <>
                    <strong>PA Type:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('paTypes', filters.paTypes.filter(option => option.id !== id))}
            />
          ))}
          {filters.isStatePaCriteriaSameAsPdlLink &&
            <Chip
              key={`isStatePaCriteriaSameAsPdlLink-${filters.isStatePaCriteriaSameAsPdlLink}`}
              chip={{
                label: (
                  <>
                    <strong>State PA Criteria Link same as State PDL Link?:</strong> {filters.isStatePaCriteriaSameAsPdlLink}
                  </>
                ),
                value: filters.isStatePaCriteriaSameAsPdlLink,
                dismissible: true
              }}
              onDismiss={() => setValue('isStatePaCriteriaSameAsPdlLink', '')}
            />
          }
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.pdlStatuses.map(({ id, label }) => (
            <Chip
              key={`pdlStatuses-${id}`}
              chip={{
                label: (
                  <>
                    <strong>PDL Status:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('pdlStatuses', filters.pdlStatuses.filter(option => option.id !== id))}
            />
          ))}
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.coverageTags.map(({ id, label }) => (
            <Chip
              key={`coverageTags-${id}`}
              chip={{
                label: (
                  <>
                    <strong>Coverage Tag:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('coverageTags', filters.coverageTags.filter(option => option.id !== id))}
            />
          ))}
          {/* @ts-expect-error TS(2532): Object is possibly 'undefined'. */}
          {filters.stepTherapyCounts.map(({ id, label }) => (
            <Chip
              key={`stepTherapyCounts-${id}`}
              chip={{
                label: (
                  <>
                    <strong>Step Therapy:</strong> {label}
                  </>
                ),
                value: id,
                dismissible: true
              }}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              onDismiss={() => setValue('stepTherapyCounts', filters.stepTherapyCounts.filter(option => option.id !== id))}
            />
          ))}
        </>
      ) : null}
    >
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="excludeNonTrackedDrugs"
          render={({ field: { value, onChange } }) => (
            <Box margin={{ bottom: '1rem' }}>
              <CheckBox
                label="Include Only Monitoring Products"
                checked={value}
                onChange={onChange}
              />
            </Box>
          )}/>
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="states"
          render={({ field: { value, onChange } }) => (
            <StateAndPoolFilter
              states={statesAsync.value ?? []}
              pools={poolsAsync.value ?? []}
              // @ts-expect-error TS(2322): Type 'MultiSelectOption<string>[] | undefined' is ... Remove this comment to see the full error message
              selected={value}
              onSelectionChange={onChange}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="clients"
          render={({ field }) => (
            // @ts-expect-error TS(2322): Type '{ lazyLoadRequest: (searchTerm: string, page... Remove this comment to see the full error message
            <LazyLoadMultiSelectList
              placeholder="Search by Clients Tracking"
              {...field}
              lazyLoadRequest={async (searchTerm, page, rpp) => {
                const result = await getClientLookups({ query: searchTerm, page, rpp });
                return result as PaginationResult<Lookup>;
              }}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="classifications"
          render={({ field: { value, onChange } }) => (
            <ClassificationFilter
              classifications={filteredClassifications ?? []}
              selected={value ?? []}
              onSelectionChange={onChange}
              label=""/>
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="products"
          render={({ field: { value, onChange } }) => (
            <ClinicalsProductFilter
              clients={selectedClients}
              classifications={selectedClassifications}
              // @ts-expect-error TS(2322): Type 'MultiSelectOption<number>[] | undefined' is ... Remove this comment to see the full error message
              selected={value}
              onSelectionChange={onChange}
              dataSource="drugs"
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="coverageTags"
          render={({ field: { value, onChange, name, onBlur, ref } }) => (
            <InMemoryMultiSelectList
              placeholder="Search by Coverage Tags"
              options={coverageTagsAsync.value ?? []}
              // @ts-expect-error TS(2322): Type 'MultiSelectOption<number>[] | undefined' is ... Remove this comment to see the full error message
              value={value}
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              ref={ref}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="stepTherapyCounts"
          render={({ field: { value, onChange, name, onBlur, ref } }) => (
            <InMemoryMultiSelectList<string>
              placeholder="Search by Step Therapy Count"
              options={STEP_THERAPY_COUNT_OPTIONS}
              value={value ?? []}
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              ref={ref}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="paTypes"
          render={({ field: { value, onChange, name, onBlur, ref } }) => (
            <InMemoryMultiSelectList
              placeholder="Search by PA Types"
              options={paTypesAsync.value ?? []}
              // @ts-expect-error TS(2322): Type 'MultiSelectOption<number>[] | undefined' is ... Remove this comment to see the full error message
              value={value}
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              ref={ref}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="pdlStatuses"
          render={({ field: { value, onChange, name, onBlur, ref } }) => (
            <InMemoryMultiSelectList
              placeholder="Search by PDL Status"
              options={pdlStatusOptions}
              // @ts-expect-error TS(2322): Type 'MultiSelectOption<PdlStatus>[] | undefined' ... Remove this comment to see the full error message
              value={value}
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              ref={ref}
            />
          )}
        />
      </Box>
      <Box width={{ min: '14rem' }}>
        <Controller
          control={control}
          name="pdlStatusBeforeDate"
          rules={{
            validate: {
              // @ts-expect-error TS(2322): Type '(value: string) => string | undefined' is no... Remove this comment to see the full error message
              valid: DateValidators.isValidDate,
            }
          }}
          render={({ field: { name, onChange, onBlur, value } }) => (
            <FormField
              required
              label={<Text color="white">PDL Status Date Until</Text>}
              name={name}
              htmlFor={`${name}`}
              margin="none"
            >
              <DateTextInput
                name={name}
                id={name}
                value={value}
                onChange={({ target }) => onChange((target as any).value)}
                onBlur={onBlur}
              />
            </FormField>
          )}
        />
      </Box>
      <Box width={{ min: '14rem', max: '10rem' }}>
        <Controller
          control={control}
          name="isStatePaCriteriaSameAsPdlLink"
          render={({ field: { value, onChange, name, onBlur, ref } }) => (
            <ListboxWithPlaceHolder
              value={value}
              onChange={onChange}
              placeholder={"State PA Criteria Link same as State PDL Link?"}
              getDisplayValue={(item: any) => {
                return item || '';
              }}
            >
              <ListboxOption key="Yes" value="Yes">
                Yes
              </ListboxOption>
              <ListboxOption key="No" value="No">
                No
              </ListboxOption>
            </ListboxWithPlaceHolder>

          )}
        />
      </Box>
    </RhfFilterForm>
  );
};
